import { useEffect, useState, useCallback } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSnackbar from "components/MDSnackbar";
import MDInput from "components/MDInput";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import httpClient from "api/terminalHttpClient";
import ConfirmationDialog from "components/Common/ConfirmationDialog";
import { CustomTablePagination } from "components/Common/CustomTablePagination";
import { CustomMenuButton } from "components/Common/CustomMenuButton";
import SchemaDialogForm from "components/Form/SchemaDialogForm";
import TerminalClaimsTableData from "./data/TerminalClaimsTableData";
import { TerminalclaimFormSchema, terminalClaimRecord } from "./data/TerminalClaimsSchemas";
import {
  terminalclaimReportFormSchema,
  terminalClaimReportRecord,
} from "./data/TerminalClaimsReportsSchemas";
import { handleApiError } from "utils/apiError";
import { formatDateTime } from "utils/datetime";

function TerminalClaims() {
  const apiBase = process.env.REACT_APP_API_URL;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalElements, setTotalElements] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogSearchOpen, setDialogSearchOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(terminalClaimRecord);
  const [formData, setFormData] = useState(terminalClaimRecord);
  const [saving, setSaving] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [terminals, setTerminals] = useState([]);
  const [terminalsLoading, setTerminalsLoading] = useState(false);
  const [isCardLoading, setIsCardLoading] = useState(false);
  const [insureeOptions, setInsureeOptions] = useState([]);
  const [policyBenefitOptions, setPolicyBenefitOptions] = useState([]);
  const [policyBenefitsLoading, setPolicyBenefitsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [isCalculatingAmount, setIsCalculatingAmount] = useState(false);
  const [searchFormData, setSearchFormData] = useState(terminalClaimReportRecord);

  const [sb, setSb] = useState({ open: false, title: "", type: "error", message: "" });
  const showSnackBar = (title, type, message) => setSb({ open: true, title, type, message });

  const loadClaims = async (q = "") => {
    try {
      setLoading(true);
      // Using search-list as per your Controller definition
      const url = q
        ? `${apiBase}/api/terminal/claims/search-list?page=${page}&size=${rowsPerPage}&q=${q}`
        : `${apiBase}/api/terminal/claims?page=${page}&size=${rowsPerPage}`;
      const res = await httpClient.get(url);
      setItems(res.data.content || []);
      setTotalElements(res.data.totalElements || 0);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setLoading(false);
    }
  };

  const loadTerminals = useCallback(
    async (search = "") => {
      try {
        setTerminalsLoading(true);
        const url = `${apiBase}/api/terminal/claims/search-terminal-list?page=0&size=20&q=${search}`;
        const res = await httpClient.get(url);
        const cardData = res.data.content || [];
        setTerminals(
          cardData.map((terminal) => ({
            ...terminal,
            label: `${terminal.code} (${terminal.hospitalName})`,
          }))
        );
      } catch (err) {
        console.error("Failed to load terminals", err);
      } finally {
        setTerminalsLoading(false);
      }
    },
    [apiBase]
  );

  const loadCardDetails = useCallback(
    async (cardNumber) => {
      if (!cardNumber) return;

      setFieldErrors((prev) => ({ ...prev, cardNumber: "" }));

      try {
        setIsCardLoading(true);
        // Path variable pattern as requested
        const url = `${apiBase}/api/claims/card-details/${cardNumber}`;
        const res = await httpClient.get(url);
        const data = res.data; // This is your ClaimCardDto

        // 1. Populate the form field for policyId
        // Assuming your form state management uses a setFormData-style hook
        setFormData((prev) => ({
          ...prev,
          policyId: data.policyId,
          policyNumber: data.policyNumber,
          cardNumber: data.card.code,
        }));

        // 2. Map InsureeDto list to label/value options
        const formattedInsurees = (data.insurees || []).map((person) => ({
          ...person,
          value: person.id,
          label: `${person.name} (${person.relationship})`,
        }));

        // 2. Map PolicyBenefitDto list to label/value options
        const formattedPolicyBenefits = (data.policyBenefits || []).map((benefit) => ({
          ...benefit,
          value: benefit.id,
          label: `${benefit.policyItem.name}`,
        }));

        setFormData((prev) => ({
          ...prev,
          cardNumber: cardNumber,
          externalErrors: {},
        }));

        setInsureeOptions(formattedInsurees);
        setPolicyBenefitOptions(formattedPolicyBenefits);
      } catch (err) {
        console.error("Failed to load card details", err);
        if (err.response && err.response.status === 404) {
          setFormData((prev) => ({
            ...prev,
            cardNumber: cardNumber,
            externalErrors: {
              cardNumber: "Card not found. Please check the number and try again.",
            },
          }));
        }
      } finally {
        setIsCardLoading(false);
      }
    },
    [apiBase, setFormData]
  );

  const calculateAmount = useCallback(
    async (amount, currentFormData) => {
      if (!amount) return;

      setFieldErrors((prev) => ({ ...prev, amount: "" }));

      try {
        setIsCalculatingAmount(true);
        // Path variable pattern as requested
        const url = `${apiBase}/api/claims/calc-amount`;
        const res = await httpClient.post(url, { ...currentFormData, amount });
        const data = res.data; // This is your ClaimCardDto

        // 1. Populate the form field for policyId
        // Assuming your form state management uses a setFormData-style hook
        setFormData((prev) => ({
          ...currentFormData,
          amountByInsurance: data.amountByInsurance,
          copayAmount: data.amountByBeneficiary,
          externalErrors: {},
        }));
      } catch (err) {
        //console.error("Failed to load card details", err);
        if (err.response && err.response.status === 400) {
          setFormData((prev) => ({
            ...prev,
            billAmount: amount,
            externalErrors: {
              billAmount:
                err.response.data.message ||
                "Failed to calculate amounts. Please check the input and try again.",
            },
          }));
        }
      } finally {
        setIsCalculatingAmount(false);
      }
    },
    [apiBase, setFormData]
  );

  useEffect(() => {
    loadClaims(searchInput);
    loadTerminals();
  }, [page, rowsPerPage]);

  const onEdit = async (rec) => {
    try {
      setLoading(true);

      const res = await httpClient.get(`${apiBase}/api/claims/${rec.id}`);
      const fullData = res.data;
      const preparedData = {
        ...fullData,
        terminalId: fullData.terminalId || null,
        cardNumber: fullData.cardNumber || null,
      };

      if (fullData.terminalId) {
        loadTerminals(fullData.terminalId);
      }

      if (fullData.cardNumber) {
        loadCardDetails(fullData.cardNumber);
      }

      console.log("Final Prepared Data:", preparedData);

      setFormData(preparedData);
      setSaving(true);
      setDialogOpen(true);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const isEdit = data.id && data.id > 0;
      const url = isEdit ? `${apiBase}/api/claims/${data.id}` : `${apiBase}/api/claims`;
      // Ensure numeric values from ClaimRequestDto are properly typed
      const payload = {
        ...data,
        amountByInsurance: Number(data.amountByInsurance || 0),
        claimTime: !isEdit ? formatDateTime(new Date()) : data.claimTime,
        isOffline: false,
        status: isEdit ? data.status : "Success",
      };

      await httpClient[isEdit ? "put" : "post"](url, payload);
      showSnackBar("Success", "success", `Claim ${isEdit ? "updated" : "created"}`);
      setDialogOpen(false);
      loadClaims(searchInput);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setSaving(false);
    }
  };

  const onAdvSearchSubmit = async (data) => {
    setSaving(true);
    try {
      const url = `${apiBase}/api/claims/export/excel`;
      const payload = {
        ...data,
        isOffline: data.submittedThrough === "offline",
        terminalId: data.terminalId || "",
        referenceId: data.referenceId || "",
      };

      // 1. Request the data with responseType 'blob'
      const response = await httpClient.post(url, payload, {
        responseType: "blob",
      });

      // 2. Create a Blob from the response data
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // 3. Create a temporary anchor element to trigger download
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      // Set the filename (you can also try to get this from headers)
      link.setAttribute("download", `Claims_Export_${new Date().getTime()}.xlsx`);
      document.body.appendChild(link);
      link.click();

      // 4. Cleanup
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      showSnackBar("Export Successful", "success", "Your file is downloading.");
      setDialogOpen(false);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setSaving(false);
    }
  };

  const onDisplaySearch = async (formData) => {
    setSaving(true);
    try {
      const url = `${apiBase}/api/terminal/claims/display-search-filtered?page=${page}&size=${rowsPerPage}`;
      // Prepare payload (matching your Export logic)
      const payload = {
        ...formData,
        isOffline: formData.submittedThrough === "offline",
        terminalId: formData.terminalId || null,
        status: formData.status,
      };

      console.log(payload);

      const res = await httpClient.post(url, payload);
      // Update the table with filtered results
      setItems(res.data.content || []);
      setTotalElements(res.data.totalElements || 0);
      showSnackBar("Success", "success", "Results updated based on filters.");
      setDialogSearchOpen(false); // Close the dialog to show the table
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setFormData({
      ...claimRecord,
      externalErrors: {},
    });
    setFieldErrors({});
    setInsureeOptions([]);
    setPolicyBenefitOptions([]);
  };

  const handleReportsDialogClose = () => {
    setDialogSearchOpen(false);
    setSearchFormData({
      ...terminalClaimReportRecord,
      externalErrors: {},
    });
    setFieldErrors({});
    setInsureeOptions([]);
    setPolicyBenefitOptions([]);
  };

  const onDeleteConfirmed = async () => {
    try {
      if (Array.isArray(itemToDelete)) {
        await httpClient.post(`${apiBase}/api/claims/bulk-delete`, { ids: itemToDelete });
      } else {
        await httpClient.delete(`${apiBase}/api/claims/${itemToDelete.id}`);
      }
      showSnackBar("Success", "success", "Deleted successfully");
      loadClaims(searchInput);
    } catch (err) {
      handleApiError(err, { showSnackBar });
    } finally {
      setConfirmationDialogOpen(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Card>
          <MDBox display="flex" justifyContent="space-between" p={3}>
            <MDBox display="flex" gap={1} />
            <MDBox display="flex" gap={1}>
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => {
                  setSearchFormData({ ...terminalClaimReportRecord });
                  setDialogSearchOpen(true);
                }}
              >
                Advanced Search
              </MDButton>
              <MDInput
                size="small"
                label="Search..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyUp={(e) => e.key === "Enter" && loadClaims(searchInput)}
              />
              <CustomMenuButton
                buttonTitle="Actions"
                items={[
                  {
                    key: "del",
                    text: "Delete Selected",
                    icon: "delete",
                    iconColor: "error",
                  },
                ]}
                onItemClicked={() => {
                  if (selectedIds.length > 0) {
                    setItemToDelete(selectedIds);
                    setConfirmationDialogOpen(true);
                  }
                }}
              />
            </MDBox>
          </MDBox>
          <MDBox px={3} pb={3}>
            <TerminalClaimsTableData
              data={items}
              selectedIds={selectedIds}
              onToggle={(id) =>
                setSelectedIds((prev) =>
                  prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
                )
              }
              rowsPerPage={rowsPerPage}
              onEdit={onEdit}
              onDelete={(rec) => {
                setItemToDelete(rec);
                setConfirmationDialogOpen(true);
              }}
            />
            <CustomTablePagination
              page={page}
              rowsPerPage={rowsPerPage}
              totalItems={totalElements}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
            />
          </MDBox>
        </Card>
      </MDBox>

      <SchemaDialogForm
        open={dialogOpen}
        title="Claim Details"
        schema={TerminalclaimFormSchema}
        initialRecord={formData}
        saving={saving}
        onClose={handleClose}
        onSubmit={onSubmit}
        deps={{
          terminals,
          onSearchTerminals: loadTerminals,
          onFetchCardDetails: loadCardDetails,
          insurees: insureeOptions,
          policyBenefits: policyBenefitOptions,
          policyBenefitsLoading: policyBenefitsLoading,
          cardLoading: isCardLoading,
          onCalculateAmount: calculateAmount,
        }}
        submitButtonText={"Submit"}
      />

      <SchemaDialogForm
        open={dialogSearchOpen}
        title="Advanced Search"
        schema={terminalclaimReportFormSchema}
        initialRecord={searchFormData}
        saving={saving}
        onClose={handleReportsDialogClose}
        onSubmit={onAdvSearchSubmit}
        deps={{
          terminals,
          onSearchTerminals: loadTerminals,
          onFetchCardDetails: loadCardDetails,
          insurees: insureeOptions,
          policyBenefits: policyBenefitOptions,
          policyBenefitsLoading: policyBenefitsLoading,
          cardLoading: isCardLoading,
          buttons: [
            {
              key: "formBtn1",
              name: "displaySearch",
              label: "Display",
              color: "primary",
              variant: "gradient",
              onClick: onDisplaySearch,
            },
          ],
        }}
        submitButtonText={"Export Report"}
      />

      <ConfirmationDialog
        open={confirmationDialogOpen}
        onCancel={() => setConfirmationDialogOpen(false)}
        onOk={onDeleteConfirmed}
        title="Confirm Delete"
        message="Are you sure you want to delete this claim record?"
      />

      <MDSnackbar
        color={sb.type}
        title={sb.title}
        content={sb.message}
        open={sb.open}
        onClose={() => setSb({ ...sb, open: false })}
      />
    </DashboardLayout>
  );
}

export default TerminalClaims;
